import apiClient from "../apiClient";

const hrAnalyticsService = {
  // Get employee count from dedicated endpoint
  getEmployeeCount: async (params = {}) => {
    try {

      const response = await apiClient.get("/analytics/employee-count", { params });

      return response.data || response;
    } catch (error) {

      throw new Error(
        error.response?.data?.responseMessage ||
        error.message ||
        "Failed to fetch employee count"
      );
    }
  },

  // Get HR dashboard summary with real data using dedicated endpoints
  getDashboardSummary: async (period = "current-month", department = "all", startDate = null, endDate = null) => {
    try {


      const params = { period, department };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      // Get employee count from dedicated endpoint with filters
      const employeeCountResponse = await apiClient.get(
        "/analytics/employee-count",
        { params }
      );


      if (employeeCountResponse.responseCode === 200) {
        const employeeCount = employeeCountResponse.data;

        // Get additional data from hr-dashboard-summary endpoint
        let additionalData = {
          activeRecruitments: 0,
          pendingLeaves: 0,
          trainingPrograms: 0,
          newHiresThisMonth: 0,
          attritionRate: 0,
          averageTenure: 0,
          departmentDistribution: {},
          recentHires: [],
          upcomingRetirements: [],
          leaveUtilization: {
            totalLeaves: 0,
            usedLeaves: 0,
            utilizationRate: 0,
          },
          performanceMetrics: {
            reviewsCompleted: 0,
            reviewsPending: 0,
            averageRating: 0,
          },
          trainingMetrics: {
            programsCompleted: 0,
            programsInProgress: 0,
            completionRate: 0,
          },
        };

        // Try to get additional data from hr-dashboard-summary endpoint
        try {
          const dashboardResponse = await apiClient.get(
            "/analytics/hr-dashboard-summary",
            { params }
          );
          if (dashboardResponse.responseCode === 200) {
            const dashboardData = dashboardResponse.data;
            additionalData = {
              activeRecruitments: dashboardData.activeRecruitments || 0,
              pendingLeaves: dashboardData.pendingLeaves || 0,
              trainingPrograms: dashboardData.trainingPrograms || 0,
              newHiresThisMonth: dashboardData.newHiresThisMonth || 0,
              attritionRate: dashboardData.attritionRate || 0,
              averageTenure: dashboardData.averageTenure || 0,
              departmentDistribution:
                dashboardData.departmentDistribution || {},
              recentHires: dashboardData.recentHires || [],
              upcomingRetirements: dashboardData.upcomingRetirements || [],
              leaveUtilization: dashboardData.leaveUtilization || {
                totalLeaves: 0,
                usedLeaves: 0,
                utilizationRate: 0,
              },
              performanceMetrics: dashboardData.performanceMetrics || {
                reviewsCompleted: 0,
                reviewsPending: 0,
                averageRating: 0,
              },
              trainingMetrics: dashboardData.trainingMetrics || {
                programsCompleted: 0,
                programsInProgress: 0,
                completionRate: 0,
              },
            };
          }
        } catch (error) {

        }

        const hrDashboardSummary = {
          totalEmployees: employeeCount.totalEmployees,
          activeEmployees: employeeCount.activeEmployees,
          inactiveEmployees: employeeCount.inactiveEmployees || 0,
          exitedEmployees: employeeCount.resignedEmployees || 0,
          ...additionalData,
        };



        return {
          responseCode: 200,
          responseMessage: "HR dashboard summary retrieved successfully",
          data: hrDashboardSummary,
        };
      } else {
        throw new Error(
          employeeCountResponse.responseMessage ||
          "Failed to fetch employee count"
        );
      }
    } catch (error) {

      throw new Error(
        error.response?.data?.responseMessage ||
        error.message ||
        "Failed to fetch HR dashboard summary"
      );
    }
  },

  // Get attrition trends data
  getAttritionTrends: async (months = 6, period = null, startDate = null, endDate = null) => {
    try {

      const params = { months };
      if (period) params.period = period;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await apiClient.get(
        `/analytics/attrition-trends`,
        { params }
      );

      return response.data || response;
    } catch (error) {

      throw new Error(
        error.response?.data?.responseMessage ||
        error.message ||
        "Failed to fetch attrition trends"
      );
    }
  },

  getRecruitmentFunnel: async (
    period = "current-month",
    department = "all",
    startDate = null,
    endDate = null
  ) => {
    try {


      // Map frontend period values to backend period values
      const periodMap = {
        "7-days": "7-days",
        "current-month": "current-month",
        "last-month": "last-month",
        "quarter": "current-quarter",
        "year": "current-year",
        "all-data": "all-data",
      };

      const backendPeriod = periodMap[period] || period;

      // Use the dedicated analytics API endpoint that only returns stats (no full candidate data)
      const params = {
        period: backendPeriod,
        department: department,
      };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await apiClient.get("/analytics/recruitment-funnel", { params });



      if (response.responseCode === 200 && response.data) {
        const funnelData = response.data;


        // Backend already consolidates status breakdown to match CandidatePipeline stages
        // Just ensure all required fields are present (all 8 stages)
        const statusBreakdown = {
          applied: funnelData.statusBreakdown?.applied || 0,
          screening: funnelData.statusBreakdown?.screening || 0,
          shortlisted: funnelData.statusBreakdown?.shortlisted || 0,
          interview: funnelData.statusBreakdown?.interview || 0, // Interview Scheduled
          interviewed: funnelData.statusBreakdown?.interviewed || 0,
          offer: funnelData.statusBreakdown?.offer || 0,
          hired: funnelData.statusBreakdown?.hired || 0,
          rejected: funnelData.statusBreakdown?.rejected || 0,
        };

        return {
          ...funnelData,
          statusBreakdown,
        };
      }

      throw new Error("No recruitment funnel data received");
    } catch (error) {

      throw new Error(
        error.response?.data?.responseMessage ||
        error.message ||
        "Failed to fetch recruitment funnel"
      );
    }
  },

  // Get department-wise attrition data
  getDepartmentAttrition: async (params = {}) => {
    try {

      const response = await apiClient.get("/analytics/department-attrition", { params });

      return response.data || response;
    } catch (error) {

      throw new Error(
        error.response?.data?.responseMessage ||
        error.message ||
        "Failed to fetch department attrition"
      );
    }
  },

  // Get comprehensive recruitment metrics
  getRecruitmentMetrics: async (period = "current-month", startDate = null, endDate = null) => {
    try {

      const params = { period };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await apiClient.get("/analytics/recruitment-metrics", {
        params,
      });

      return response.data || response;
    } catch (error) {

      throw new Error(
        error.response?.data?.responseMessage ||
        error.message ||
        "Failed to fetch recruitment metrics"
      );
    }
  },

  // Get employee statistics
  getEmployeeStats: async () => {
    try {

      const response = await this.getDashboardSummary();

      return response;
    } catch (error) {

      throw new Error(
        error.response?.data?.responseMessage ||
        error.message ||
        "Failed to fetch employee statistics"
      );
    }
  },

  // Get interviewer performance statistics
  getInterviewerPerformance: async (
    period = "current-quarter",
    department = "all"
  ) => {
    try {

      const response = await apiClient.get(
        `/recruitment/interviews/performance?period=${period}&department=${department}`
      );


      if (response.responseCode === 200 && response.data) {
        return response.data;
      }

      throw new Error("No interviewer performance data received");
    } catch (error) {

      // Fallback to mock data if API fails

      return {
        period,
        department,
        totalInterviewers: 0,
        data: [],
      };
    }
  },

  // Get salary trends by department
  getSalaryTrends: async (period = "current-month", department = "all", startDate = null, endDate = null) => {
    try {
      const params = { period, department };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await apiClient.get(
        "/analytics/salary-trends",
        { params }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.responseMessage ||
        error.message ||
        "Failed to fetch salary trends"
      );
    }
  },

  // Get comprehensive HR statistics from Employee schema
  getComprehensiveHRStats: async (
    period = "current-month",
    department = "all",
    startDate = null,
    endDate = null
  ) => {
    try {
      const params = { period, department };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await apiClient.get(
        "/analytics/hr-comprehensive-stats",
        { params }
      );


      // Handle the nested response structure
      if (response.data && response.data.data) {
        return response.data.data;
      } else if (response.data) {
        return response.data;
      } else {
        return null;
      }
    } catch (error) {

      throw new Error(
        error.response?.data?.responseMessage ||
        error.message ||
        "Failed to fetch comprehensive HR statistics"
      );
    }
  },

  // Get interviewer performance statistics
  getInterviewerPerformanceStats: async (
    period = "current-month",
    startDate = null,
    endDate = null
  ) => {
    try {

      const params = { period };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await apiClient.get("/analytics/interviewer-performance", {
        params,
      });
      return response.data || response;
    } catch (error) {
      throw new Error(
        error.response?.data?.responseMessage ||
        error.message ||
        "Failed to fetch interviewer performance statistics"
      );
    }
  },

  // Get comprehensive salary trends
  getComprehensiveSalaryTrends: async (
    period = "current-month",
    department = "all",
    startDate = null,
    endDate = null
  ) => {
    try {
      const params = { period, department };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await apiClient.get("/analytics/salary-trends-comprehensive", {
        params
      });
      return response.data || response;
    } catch (error) {
      throw new Error(
        error.response?.data?.responseMessage ||
        error.message ||
        "Failed to fetch comprehensive salary trends"
      );
    }
  },
};

export default hrAnalyticsService;
